var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { observable } from "mobx";
import { RemoteData } from "../Infra/RemoteData";
export class GlobalAdminRetrievalManager {
    constructor(globalAdminRetrievalService, invalidator) {
        this.globalAdminRetrievalService = globalAdminRetrievalService;
        this.actionLoading = false;
        this.currentPatientDetails = null;
        this.loadingPatientDetails = false;
        this.batchDetailsById = new Map();
        this.loadingBatchIds = new Set();
        this.searchResults = [];
        this.searchLoading = false;
        this.patientsWithErrors = [];
        this.patientsWithErrorsLoading = false;
        this.retryingPatientIds = new Set();
        this.batchDetailsGeneration = 0;
        this.summary = new RemoteData(invalidator, async () => {
            return this.globalAdminRetrievalService.getMigrationSummary();
        });
    }
    invalidateBatchDetailsCache() {
        this.batchDetailsGeneration++;
        this.batchDetailsById = new Map();
        this.loadingBatchIds = new Set();
    }
    async loadPatientDetails(patientId) {
        this.loadingPatientDetails = true;
        this.invalidateBatchDetailsCache();
        try {
            const details = await this.globalAdminRetrievalService.getPatientDetails(patientId);
            this.currentPatientDetails = details;
            return details;
        }
        catch {
            this.currentPatientDetails = null;
            return null;
        }
        finally {
            this.loadingPatientDetails = false;
        }
    }
    async loadBatchDetails(patientId, batchId, options) {
        if (!(options === null || options === void 0 ? void 0 : options.forceReload)) {
            const cached = this.batchDetailsById.get(batchId);
            if (cached) {
                return cached;
            }
        }
        else {
            const updatedMap = new Map(this.batchDetailsById);
            updatedMap.delete(batchId);
            this.batchDetailsById = updatedMap;
        }
        const generation = this.batchDetailsGeneration;
        const loadingSet = new Set(this.loadingBatchIds);
        loadingSet.add(batchId);
        this.loadingBatchIds = loadingSet;
        try {
            const details = await this.globalAdminRetrievalService.getBatchDetails(patientId, batchId);
            if (generation !== this.batchDetailsGeneration) {
                return null;
            }
            const updatedMap = new Map(this.batchDetailsById);
            updatedMap.set(batchId, details);
            this.batchDetailsById = updatedMap;
            return details;
        }
        catch {
            return null;
        }
        finally {
            if (generation === this.batchDetailsGeneration) {
                const updatedLoadingSet = new Set(this.loadingBatchIds);
                updatedLoadingSet.delete(batchId);
                this.loadingBatchIds = updatedLoadingSet;
            }
        }
    }
    isBatchLoading(batchId) {
        return this.loadingBatchIds.has(batchId);
    }
    getBatchDetails(batchId) {
        return this.batchDetailsById.get(batchId);
    }
    async migratePatient(patientId) {
        this.actionLoading = true;
        try {
            const result = await this.globalAdminRetrievalService.migratePatient(patientId);
            this.summary.reload();
            await this.loadPatientDetails(patientId);
            return result;
        }
        finally {
            this.actionLoading = false;
        }
    }
    refresh() {
        this.summary.reload();
    }
    clearCurrentPatient() {
        this.currentPatientDetails = null;
        this.invalidateBatchDetailsCache();
    }
    async searchPatients(query) {
        this.searchLoading = true;
        try {
            const results = await this.globalAdminRetrievalService.searchPatients(query);
            this.searchResults = results;
            return results;
        }
        catch {
            this.searchResults = [];
            return [];
        }
        finally {
            this.searchLoading = false;
        }
    }
    clearSearchResults() {
        this.searchResults = [];
    }
    async loadPatientsWithVectorizationErrors(limit = 100, sinceHours = 24 * 7) {
        this.patientsWithErrorsLoading = true;
        try {
            const result = await this.globalAdminRetrievalService.listPatientsWithVectorizationErrors(limit, sinceHours);
            this.patientsWithErrors = result.patients;
        }
        catch {
            this.patientsWithErrors = [];
        }
        finally {
            this.patientsWithErrorsLoading = false;
        }
    }
    async retryFailedEmbeddingChunks(patientId) {
        const retryingSet = new Set(this.retryingPatientIds);
        retryingSet.add(patientId);
        this.retryingPatientIds = retryingSet;
        try {
            return await this.globalAdminRetrievalService.retryFailedEmbeddingChunks(patientId);
        }
        finally {
            const updatedSet = new Set(this.retryingPatientIds);
            updatedSet.delete(patientId);
            this.retryingPatientIds = updatedSet;
        }
    }
}
__decorate([
    observable
], GlobalAdminRetrievalManager.prototype, "actionLoading", void 0);
__decorate([
    observable
], GlobalAdminRetrievalManager.prototype, "currentPatientDetails", void 0);
__decorate([
    observable
], GlobalAdminRetrievalManager.prototype, "loadingPatientDetails", void 0);
__decorate([
    observable
], GlobalAdminRetrievalManager.prototype, "batchDetailsById", void 0);
__decorate([
    observable
], GlobalAdminRetrievalManager.prototype, "loadingBatchIds", void 0);
__decorate([
    observable
], GlobalAdminRetrievalManager.prototype, "searchResults", void 0);
__decorate([
    observable
], GlobalAdminRetrievalManager.prototype, "searchLoading", void 0);
__decorate([
    observable
], GlobalAdminRetrievalManager.prototype, "patientsWithErrors", void 0);
__decorate([
    observable
], GlobalAdminRetrievalManager.prototype, "patientsWithErrorsLoading", void 0);
__decorate([
    observable
], GlobalAdminRetrievalManager.prototype, "retryingPatientIds", void 0);
